import React, { useState, useEffect } from 'react';
import { Typography, Container, Button, Box } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import FeatureCard from '../shared-components/FeatureCard';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';
import { featuresData } from './featuresData';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';

function Features() {
  const [searchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState('all');
  const [imageOpen, setImageOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  const openImage = (imageSrc) => {
    setCurrentImage(imageSrc);
    setImageOpen(true);
  };

  // Read filter from URL params on mount
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && ['operations', 'network', 'care'].includes(filterParam)) {
      setActiveFilter(filterParam);
    }
  }, [searchParams]);

  const filteredFeatures = activeFilter === 'all'
    ? featuresData
    : featuresData.filter(f => 
        Array.isArray(f.pillars) ? f.pillars.includes(activeFilter) : f.pillar === activeFilter
      );

  const filterButtons = [
    { id: 'all', label: 'All Features' },
    { id: 'operations', label: 'Operations' },
    { id: 'network', label: 'Network' },
    { id: 'care', label: 'Care' }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-50 via-white to-white">
        <Container maxWidth="lg">
          <div className="text-center max-w-4xl mx-auto">
            <Typography
              variant="h1"
              align="center"
              className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
            >
              Everything You Need to Run a{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Modern Mental Health Practice
              </span>
            </Typography>

            <Typography
              variant="h2"
              align="center"
              className="text-lg lg:text-xl text-gray-600 mb-12 font-normal leading-relaxed"
            >
              One integrated ecosystem. No more silos. Halespring combines practice management, clinical documentation, billing, scheduling, networking, and collaboration into one intelligent system.
            </Typography>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
              <Button
                component={Link}
                to="/sign-up"
                variant="contained"
                size="large"
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 text-base font-bold shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
              >
                Get Started Free
              </Button>
              <Button
                component={Link}
                to="/pricing"
                variant="outlined"
                size="large"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-3.5 text-base font-semibold"
              >
                View Pricing
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 opacity-80">
              <TrustBadges />
            </div>
          </div>
        </Container>
      </section>

      {/* INTERACTIVE FEATURE GRID */}
      <section className="py-24 bg-white shadow-inner">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Explore Our Features
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Discover the complete suite of tools designed for mental health professionals.
            </Typography>
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {filterButtons.map(button => (
              <Button
                key={button.id}
                onClick={() => setActiveFilter(button.id)}
                variant={activeFilter === button.id ? 'contained' : 'outlined'}
                className={`px-6 py-2 font-semibold transition-all ${
                  activeFilter === button.id
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {button.label}
              </Button>
            ))}
          </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {filteredFeatures.map(feature => (
              <FeatureCard
                key={feature.id}
                icon={<feature.icon sx={{ fontSize: 40 }} />}
                title={feature.title}
                description={feature.shortDescription}
                pillar={feature.pillar}
                pillars={feature.pillars}
                slug={feature.slug}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* INTEGRATION WORKFLOWS */}
      <section className="py-24 bg-blue-50">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              The Power of Integration
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              See how Halespring features work together seamlessly to create powerful end-to-end workflows.
            </Typography>
          </div>

          {/* Workflow Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {[
              {
                image: 'assets/images/marketing/features/client-lifecycle-workflow.jpg',
                stat: '80% time savings'
              },
              {
                image: 'assets/images/marketing/features/ai-onboarding-workflow.jpg',
                stat: '5 min vs 45 min'
              },
              {
                image: 'assets/images/marketing/features/collaboration-workflow.jpg',
                stat: 'Hours vs days'
              },
              {
                image: 'assets/images/marketing/features/billing-workflow.jpg',
                stat: '< 2 minutes'
              }
            ].map((workflow, index) => (
              <div key={index} className="bg-white p-6 rounded-2xl shadow-lg">
                <img 
                  src={workflow.image} 
                  alt="Workflow diagram" 
                  className="w-full rounded-lg mb-4 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => openImage(workflow.image)}
                />
                <div className="flex justify-center">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg inline-block">
                    <div className="px-6 py-3">
                      <Typography className="text-sm font-bold text-white">
                        {workflow.stat}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* FINAL CTA SECTION */}
      <CTASection
        headline="Ready to Transform Your Practice?"
        copy="Join thousands of mental health professionals using Halespring to reduce admin time and improve patient outcomes."
        primaryCTA="Get Started Free"
        secondaryCTA="View Pricing"
        trustElements={[
          "Free account - no card needed",
          "Premium trial available",
          "HIPAA BAA included",
          "Cancel anytime"
        ]}
      />

      {/* Image Lightbox */}
      <Lightbox
        open={imageOpen}
        close={() => setImageOpen(false)}
        slides={currentImage ? [{ src: currentImage }] : []}
      />
    </div>
  );
}

export default Features;